/**
 * The application scene machine — one transition table, no scattered booleans.
 *
 * This is deliberately *not* `Game.scene`. That field answers a different
 * question: which world to build (`'world' | 'lab' | 'prologue'`), fixed at
 * construction and never changed afterwards. Conflating "which terrain
 * generator" with "which screen the player is looking at" is what produced the
 * previous shape of `main.ts`, where the boot path was a chain of early returns
 * and nested closures and there was no single place that said what the legal
 * screens were.
 *
 * So: `Game.scene` stays the world kind, `AppScene` is the screen, and the two
 * meet in exactly one place (`main.ts`) — a `playing` scene constructs a Game
 * with a world kind.
 *
 * Pure by design. It imports nothing, mutates nothing, and returns `null` for a
 * transition that is not on the table rather than half-taking it: a menu that
 * silently ignores a button is indistinguishable from one that is broken, and
 * the caller can log the refusal because it can see it happen.
 */

export type AppScene = 'title' | 'settings' | 'runSetup' | 'prologue' | 'playing';

/** Every scene, for the reachability test. Keep in sync with `AppScene`. */
export const SCENES: readonly AppScene[] = [
  'title',
  'settings',
  'runSetup',
  'prologue',
  'playing',
];

export type SceneEvent =
  /** Title → the prologue on a fresh profile, otherwise straight to setup. */
  | 'newRun'
  /** The prologue reached its last beat, or was skipped. */
  | 'prologueDone'
  /** The setup screen's Start button; a Game now exists. */
  | 'runStarted'
  /**
   * The title's Continue button — issue #51. Its own edge rather than a reuse
   * of `runStarted`, because it starts from a different screen and skips the
   * one in between: the archetype, kit, mode and seed are already decided by
   * the run being resumed, so walking the player through the screen that
   * decides them would be a lie about what their choices there would do.
   */
  | 'continueRun'
  /** From the title, or from the in-run pause menu. */
  | 'openSettings'
  /** Back to whichever screen opened settings. */
  | 'closeSettings'
  /** Pause → title. Tears the run down. */
  | 'quitToTitle'
  /** The hand-built movement chamber (`?scene=lab`), reachable from the title. */
  | 'openLab'
  /**
   * The title's Prologue button. Replaces the old "play the prologue" setting
   * — a toggle that only changed what `newRun` did later reads as a hidden
   * mode, where a button that runs the prologue right now reads as what it
   * is. Legal from the title regardless of `prologueSeen`; a returning player
   * asking to see it again is not a first-time player, and the button does
   * not pretend otherwise.
   */
  | 'openPrologue';

export interface SceneState {
  scene: AppScene;
  /**
   * Where `closeSettings` goes. Carried on the state rather than inferred,
   * because the settings screen is genuinely reachable from two places and
   * "guess where we came from" is how a back button starts lying.
   */
  returnTo: Exclude<AppScene, 'settings'>;
}

export interface SceneContext {
  /** `ProfileData.settings.prologueSeen` — decides the first hop out of the title. */
  prologueSeen: boolean;
}

export function initialScene(): SceneState {
  return { scene: 'title', returnTo: 'title' };
}

/**
 * Take one edge. Returns a *new* state, or null if the event is not legal from
 * the current scene.
 */
export function applyScene(
  state: SceneState,
  event: SceneEvent,
  ctx: SceneContext,
): SceneState | null {
  const { scene, returnTo } = state;

  if (event === 'openSettings') {
    // Reachable from the two places the player actually is: standing at the
    // title, or paused mid-run. Not from setup — that screen is a decision in
    // progress and losing it to a submenu is worse than waiting.
    if (scene !== 'title' && scene !== 'playing') return null;
    return { scene: 'settings', returnTo: scene };
  }

  if (event === 'closeSettings') {
    if (scene !== 'settings') return null;
    return { scene: returnTo, returnTo };
  }

  if (event === 'quitToTitle') {
    // Only from somewhere there is something to leave.
    if (scene !== 'playing' && scene !== 'runSetup' && scene !== 'prologue') return null;
    return { scene: 'title', returnTo: 'title' };
  }

  switch (scene) {
    case 'title':
      if (event === 'newRun') {
        // docs/08 §2: the prologue runs once per profile, before setup, because
        // the run's poverty cannot mean anything if being stripped is the first
        // thing you ever see.
        return { scene: ctx.prologueSeen ? 'runSetup' : 'prologue', returnTo: 'title' };
      }
      // Only offered when `SaveSystem.load().run` holds a run that has not
      // ended — the title screen builds the button from the same predicate, so
      // an edge taken with nothing to resume cannot be reached from the UI.
      if (event === 'continueRun') return { scene: 'playing', returnTo: 'title' };
      if (event === 'openLab') return { scene: 'playing', returnTo: 'title' };
      // Unconditional, unlike `newRun`'s branch above: the button is the
      // player asking to see the prologue *now*, so whether their profile has
      // already marked it seen is not this edge's business — it is
      // `Game.prologueSkipOffered`'s, which reads the same flag directly and
      // is what makes a repeat viewing skippable while a first one is not.
      if (event === 'openPrologue') return { scene: 'prologue', returnTo: 'title' };
      return null;

    case 'prologue':
      return event === 'prologueDone' ? { scene: 'runSetup', returnTo: 'title' } : null;

    case 'runSetup':
      return event === 'runStarted' ? { scene: 'playing', returnTo: 'title' } : null;

    default:
      return null;
  }
}
